import './Header.sass';

import PropTypes from 'prop-types';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { CSVLink } from 'react-csv';

import DropDown from '../../../../commonComponents/DropDown/DropDown';
import ConfirmDeletion from '../../../../commonComponents/Modals/ConfirmDeletion/ConfirmDeletion';
import ImportCSV from '../../../../commonComponents/Modals/ImportCSV/ImportCSV';
import { isGptQuestionGenerator } from '../../../../utilities/isGptQuestionGenerator';
import { isWriterOnlyAdmin } from '../../../../utilities/isWriterOnlyAdmin';
import {
  allowRolesPermissions,
  downloadXLSX,
  generateDownloadFilename,
  hasDownloadCSV,
  processQuestionsForExport,
} from '../../../../utilities/questionExport';
import { getAiTaskByChapterService } from '../../../../utilities/ServiceManager';
import ViewActivityContainer from '../../../Books/components/ViewActivityContainer';
import UploadInput from '../UploadInput/UploadInput';
import AIQuestionNotification from './components/AIQuestionNotification';
import { AI_TASK_STATUS } from './constants/constants';

export default class Header extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isDownloadXLSX: false,
      isDownloadXLS: false,
      aiQuestionGeneration: null,
      isLoadingAiQuestionGeneration: false,
    };

    this.aiQuestionGenerationInterval = null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.chapter?.id !== this.props.chapter?.id) {
      this.clearAiQuestionGenerationInterval();
      this.setState({
        aiQuestionGeneration: null,
        isLoadingAiQuestionGeneration: false,
      });
      return;
    }

    // AI Question Generation logic
    if (
      this.props.chapter &&
      this.props.chapter.id &&
      !this.state.aiQuestionGeneration &&
      !this.state.isLoadingAiQuestionGeneration
    ) {
      this.loadAiQuestionGeneration();
    }

    // === Handling XLSX/XLS downloads ===
    // When questionsCSV changes and we have XLSX download flag
    if (
      prevProps.questionsCSV !== this.props.questionsCSV &&
      this.state.isDownloadXLSX
    ) {
      downloadXLSX(this.props.questionsCSV, 'xlsx', this.props.chapter);
      this.setState({ isDownloadXLSX: false });
      return; // exit to not execute the CSV click
    }

    // When questionsCSV changes and we have XLS download flag
    if (
      prevProps.questionsCSV !== this.props.questionsCSV &&
      this.state.isDownloadXLS
    ) {
      downloadXLSX(this.props.questionsCSV, 'xls', this.props.chapter);
      this.setState({ isDownloadXLS: false });
      return; // exit to not execute the CSV click
    }

    // CSV download trigger - ONLY if it is not XLSX or XLS
    if (
      prevProps.questionsCSV !== this.props.questionsCSV &&
      !this.state.isDownloadXLSX &&
      !this.state.isDownloadXLS
    ) {
      document.getElementById('questions-download-csv').click();
    }
  }

  componentWillUnmount() {
    this.clearAiQuestionGenerationInterval();
    this.props.clearQuestionsToDownload();
  }

  getHasGPTPermissions = () => {
    if (!this.props.user || !this.props.user.roles) {
      return false;
    }

    return !!(
      (Array.isArray(this.props.user.roles) &&
        this.props.user.roles.some(
          (role) => role?.name === 'Create GPT questions',
        )) ||
      this.props.user.roles?.name === 'Create GPT questions' ||
      this.props.user.isSuperAdmin
    );
  };

  clearAiQuestionGenerationInterval = () => {
    if (this.aiQuestionGenerationInterval) {
      clearInterval(this.aiQuestionGenerationInterval);
      this.aiQuestionGenerationInterval = null;
    }
  };

  startAiQuestionGenerationPolling = () => {
    if (this.aiQuestionGenerationInterval) return;

    this.aiQuestionGenerationInterval = setInterval(() => {
      this.loadAiQuestionGeneration();
    }, 30000);
  };

  loadAiQuestionGeneration = async () => {
    if (!this.props.chapter?.id || this.state.isLoadingAiQuestionGeneration) {
      return;
    }

    this.setState({
      isLoadingAiQuestionGeneration: true,
    });

    const getAiQuestionGenerationStatus = this.getHasGPTPermissions()
      ? await getAiTaskByChapterService(this.props.chapter.id)
      : null;

    if (
      getAiQuestionGenerationStatus &&
      !getAiQuestionGenerationStatus.deletedAt
    ) {
      const { id, status, createdAt, deletedAt, executionTime } =
        getAiQuestionGenerationStatus;

      const getTimeElapsed = () => {
        if (status === AI_TASK_STATUS.IN_PROGRESS) {
          return Math.floor((Date.now() - createdAt) / 1000);
        }
        return Math.floor(executionTime);
      };

      const wasInProgress =
        this.state.aiQuestionGeneration?.status === AI_TASK_STATUS.IN_PROGRESS;

      this.setState({
        aiQuestionGeneration: {
          id,
          status,
          timeElapsed: getTimeElapsed(),
          createdAt,
          deletedAt,
        },
        isLoadingAiQuestionGeneration: false,
      });

      if (status === AI_TASK_STATUS.IN_PROGRESS) {
        this.startAiQuestionGenerationPolling();
      } else {
        this.clearAiQuestionGenerationInterval();

        if (status === AI_TASK_STATUS.FINISHED && wasInProgress) {
          this.props.loadQuestionsOfChapter(
            this.props.assignment?.id
              ? this.props.assignment.chapter.id
              : this.props.chapter.id,
            1,
            1000,
            this.props.assignment?.id ?? null,
            null,
            undefined,
            this.props.isShowDeletedQuestions,
          );
        }
      }
    } else {
      this.clearAiQuestionGenerationInterval();

      this.setState({
        aiQuestionGeneration: 'none',
        isLoadingAiQuestionGeneration: false,
      });
    }
  };

  getCurrentURL = () => {
    const url = window.location.href;
    return url.split('/');
  };

  getSubtopics = (subtopics, topics, onDeleteSubtopic, onAddSubtopic, rate) => {
    if (rate && rate.id === 0) {
      return 'Subtopics are not displayed while accessing chapters from the All tab';
    }
    let subs = [];
    if (subtopics && topics) {
      subs = subtopics.map((subtopic, index) => {
        const topic = topics.find((topic) => subtopic.topic === topic.id);
        if (topic) {
          return (
            <Subtopic
              subtopic={subtopic}
              topic={topic}
              onDeleteSubtopic={onDeleteSubtopic}
              editPermission={this.props.editPermission}
              key={index}
            />
          );
        }
        return null;
      });
    }
    if (this.props.editPermission) {
      subs.push(
        <div className="PlusBox" onClick={onAddSubtopic} key={subs.length}>
          <i className="fa fa-plus fa-lg mt-1" />
        </div>,
      );
    }

    return subs;
  };

  openFileImport = () => {
    const assignmentId = this.props.assignment
      ? this.props.assignment.id
      : null;

    this.props.openModal({
      modalContent: (
        <ImportCSV
          onConfirm={(file) => this.onImportQuestions(file, assignmentId)}
          onCancel={this.props.closeModal}
          onShowNotification={this.props.onShowNotification}
          useRawFile
        />
      ),
      modalClassName: 'modal-import-questions',
      className: '',
    });
  };

  onImportQuestions = (file) => {
    const chapterId = this.props.chapter?.id;
    const assignmentId = this.props.assignment?.id || null;

    if (!chapterId) {
      this.props.onShowNotification('No chapter selected', 'error');
      return;
    }
    this.props.importQuestionsFromCSV(chapterId, file, assignmentId);
  };

  exportQuestions = () => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId) {
      this.props.onShowNotification('No chapter selected', 'error');
      return;
    }
    this.props.exportQuestionsToCSV(chapterId);
  };

  onDelete = () => {
    this.props.openModal({
      modalTitle: 'Delete all questions',
      modalContent: (
        <ConfirmDeletion
          onSubmit={() =>
            this.props.deleteAllQuestions(
              this.props.chapter,
              this.props.assignment,
            )
          }
          text="You are about to delete all questions of this chapter"
          onCancel={this.props.closeModal}
        />
      ),
    });
  };

  onDeleteSeletedQuestions = () => {
    this.props.openModal({
      modalTitle: this.props.isShowDeletedQuestions
        ? 'Undelete Selected Questions'
        : 'Delete selected questions',
      modalContent: (
        <ConfirmDeletion
          onSubmit={() => {
            if (this.props.isShowDeletedQuestions) {
              this.props.restoreQuestion(
                this.props.questionsForDeleted,
                this.props.assignment ? this.props.assignment.id : null,
              );
              this.props.setSeletedQuestionsForDeleted('', false);
              this.props.toggleDeletedQuestionsView();
            } else {
              this.props.deleteQuestion(
                this.props.questionsForDeleted,
                this.props.assignment ? this.props.assignment.id : null,
              );
              this.props.setSeletedQuestionsForDeleted('', false);
            }
          }}
          text={`You are about to ${
            this.props.isShowDeletedQuestions ? 'undelete' : 'delete'
          } selected questions of this chapter`}
          onCancel={this.props.closeModal}
          questionsSelected={this.props.questionsForDeleted}
          setSeletedQuestionsForDeleted={
            this.props.setSeletedQuestionsForDeleted
          }
          isShowDeletedQuestions={this.props.isShowDeletedQuestions}
          isDelete
        />
      ),
    });
  };

  getText = (text) => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    return text;
  };

  toggleDeletedSubmitQuestionsView = () => {
    const chapter = this.props.chapter;
    if (chapter) {
      this.props.loadQuestionsOfChapter(
        chapter.id,
        1,
        50,
        null,
        false,
        0,
        false,
        false,
        null,
        'viewDeletedByAdmin',
      );
    }
  };

  toggleSubmitedQuestionsView = () => {
    const chapter = this.props.chapter;
    if (chapter) {
      this.props.loadQuestionsOfChapter(
        chapter.id,
        1,
        50,
        null,
        false,
        0,
        false,
        false,
        null,
        'viewSubmitedQuestion',
      );
    }
  };

  togglePublishedSubmitQuestionsView = () => {
    const chapter = this.props.chapter;
    if (chapter) {
      this.props.loadQuestionsOfChapter(
        chapter.id,
        1,
        50,
        null,
        false,
        0,
        false,
        false,
        null,
        'viewPublishedSubmitedQuestion',
      );
    }
  };

  getChapterOptions = () => {
    const {
      permissions,
      generalRolesPermissions,
      toggleDeletedQuestionsView,
      isShowDeletedQuestions,
      editPermission,
    } = this.props;

    const options = [];
    if (
      this.props.reference &&
      this.props.reference.name !== 'SUBMIT QUESTIONS'
    ) {
      if (editPermission) {
        options.push(
          {
            name: 'Import Questions',
            event: this.openFileImport,
          },
          {
            name: 'Export Questions',
            event: this.exportQuestions,
          },
          {
            name: 'Delete All Questions',
            event: this.onDelete,
          },
        );
      }
      options.push({
        name: isShowDeletedQuestions
          ? 'View Published Questions'
          : 'View Deleted Questions',
        event: toggleDeletedQuestionsView,
      });
      options.push({
        name: isShowDeletedQuestions
          ? 'Undelete Selected Questions'
          : 'Delete Selected Questions',
        event: this.onDeleteSeletedQuestions,
      });
      const permission = hasDownloadCSV(generalRolesPermissions);
      const rolesPerm = allowRolesPermissions(permissions);
      if (permission || rolesPerm) {
        options.push({
          name: 'View Chapter Activity',
          event: this.openViewChapterActivityModal,
        });
        options.push(
          {
            name: 'Download CSV',
            event: this.setQuestionsCSV,
          },
          {
            name: 'Download XLSX',
            event: this.setQuestionsXLSX,
          },
          {
            name: 'Download XLS',
            event: this.setQuestionsXLS,
          },
          {
            name: 'View Chapter Activity',
            event: this.openViewChapterActivityModal,
          },
        );
      }
    }
    if (editPermission) {
      options.push(
        {
          name: 'View Deleted by Admin',
          event: this.toggleDeletedSubmitQuestionsView,
        },
        {
          name: 'View Submitted Questions',
          event: this.toggleSubmitedQuestionsView,
        },
        {
          name: 'View Published Questions',
          event: this.togglePublishedSubmitQuestionsView,
        },
      );

      if (!isWriterOnlyAdmin(this.props.user)) {
        options.push({
          name: 'Make restricted',
          event: this.props.restrictChapterFurtherReading,
        });
      }
    }
    return options;
  };

  openViewChapterActivityModal = () => {
    this.props.openModal({
      modalContent: (
        <ViewActivityContainer
          closeModal={this.props.closeModal}
          id={this.props.chapter.id}
          isChapter
        />
      ),
      modalClassName: 'ActivityModal',
    });
  };

  setQuestionsCSV = () => {
    const isInWheelHousePage = this.getCurrentURL().includes('wheelhouse');
    const isWritersPage = this.getCurrentURL().includes('writers');
    const isAssignmentsPage = this.getCurrentURL().includes('assignments');
    const isMyAssignmentsPage = this.getCurrentURL().includes('my-assignments');
    const isAssignment =
      isInWheelHousePage ||
      isWritersPage ||
      isAssignmentsPage ||
      isMyAssignmentsPage;
    const reference = this.props.reference;
    const chapter = this.props.chapter;
    const filteredReference = { ...reference };

    if (chapter && !isAssignment) {
      filteredReference.chapters = reference.chapters.filter(
        (c) => c.id === chapter.id,
      );
    }
    const idChapter = !isAssignment
      ? filteredReference.chapters[0].id
      : chapter.id;
    this.props.loadChapterQuestionsCSV(idChapter, true, isAssignment);
  };

  setQuestionsXLSX = () => {
    const chapter = this.props.chapter;
    if (chapter) {
      this.props.loadChapterQuestionsCSV(chapter.id, true, false);
      this.setState({ isDownloadXLSX: true });
    }
  };

  setQuestionsXLS = () => {
    const chapter = this.props.chapter;
    if (chapter) {
      this.props.loadChapterQuestionsCSV(chapter.id, true, false);
      this.setState({ isDownloadXLS: true });
    }
  };

  getDisabledBar = (assignments) => {
    const assignment = assignments[0];
    if (assignment) {
      const writer = assignment.primaryWriter || {};
      return (
        <div className="DisabledChapterBar">
          This chapter has been assigned to {writer.name} {writer.lastName} and
          cannot be edited until published.
        </div>
      );
    }
  };

  onHeaderBackClick = () => {
    const { isWaiting, rate, historyObject, isQual, onHeaderBack } = this.props;
    this.props.setCurrentPage(0);

    if (!isWaiting) {
      onHeaderBack({
        selectedRate: rate || { id: 0 },
        historyObject,
        isQual,
      });
    }
  };

  render() {
    const {
      reference,
      chapter,
      subtopics,
      topics,
      onDeleteSubtopic,
      onShowDefinitions,
      onAddSubtopics,
      selectedView,
      deletePdfChapter,
      updateQuestionPdfProcessing,
      questionPdfProcessing,
      updateChapter,
      rate,
      assignment,
    } = this.props;

    const showDisabledBar = !assignment && chapter?.assignments?.length;
    const chapterOptions = showDisabledBar ? [] : this.getChapterOptions();

    const canCreateGPTQuestions = isGptQuestionGenerator(
      this.props.user.roles,
      this.props.user.isSuperAdmin,
    );

    return (
      <Row className={showDisabledBar ? 'HeaderRow noPaddingTop' : 'HeaderRow'}>
        {showDisabledBar ? this.getDisabledBar(chapter.assignments) : ''}
        <Col md={12} className="header__title">
          <div className="header__left">
            <span
              className="fa fa-arrow-left fa-lg mt-1 pointer"
              data-test="back-button"
              onClick={this.onHeaderBackClick}
            />
            <span className="ReferenceName">
              {assignment?.chapter?.reference?.name || reference?.name || ''} -{' '}
              {chapter &&
                chapter.name +
                  (chapter.description
                    ? ` - ${chapter.description}`
                    : reference.description
                      ? ` - ${reference.description}`
                      : '')}
            </span>
          </div>
          <div className="header__right">
            {this.state.aiQuestionGeneration &&
            !this.state.aiQuestionGeneration.deletedAt &&
            canCreateGPTQuestions ? (
              <AIQuestionNotification
                aiQuestionGeneration={this.state.aiQuestionGeneration}
                deleteNotification={this.props.deleteNotification}
              />
            ) : null}
            {chapterOptions.length > 0 ? (
              <div style={{ marginLeft: 'auto' }}>
                <DropDown
                  data-test="book-options-dropdown"
                  options={chapterOptions}
                  shouldCloseAfter
                  containerStyle={{ float: 'right', marginLeft: 'auto' }}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    zIndex: '5',
                    marginTop: '10px',
                  }}
                />
              </div>
            ) : (
              ''
            )}
          </div>
        </Col>
        <Col md={12}>
          {!this.props.isQual &&
            topics?.length > 0 &&
            this.getSubtopics(
              subtopics,
              topics,
              onDeleteSubtopic,
              onAddSubtopics,
              rate,
            )}
          {chapter?.id && (
            <UploadInput
              reference={reference}
              imageUrl={chapter.pdfUrl}
              chapter={chapter}
              deletePdfChapter={deletePdfChapter}
              updateQuestionPdfProcessing={updateQuestionPdfProcessing}
              questionPdfProcessing={questionPdfProcessing}
              updateChapter={updateChapter}
              isWaiting={this.props.isWaiting}
              editPermission={this.props.editPermission}
              isPrivatePDF
              urlPdfPrivate={this.props.urlPdfPrivate}
              getPrivateUrl={this.props.getUrlPdf}
            />
          )}
          {selectedView !== 'gridView' && (
            <div onClick={onShowDefinitions} className="Definitions">
              Definitions
            </div>
          )}
        </Col>
        <div style={{ display: 'none' }}>
          <CSVLink
            id="questions-download-csv"
            filename={generateDownloadFilename(this.props.chapter, 'csv')}
            data={processQuestionsForExport(this.props.questionsCSV) ?? ''}
            headers={[
              { label: 'questionID', key: 'questionID' },
              { label: 'AI_question', key: 'AI_question' },
              { label: 'AI_question_change', key: 'AI_question_change' },
              { label: 'AI_answer', key: 'AI_answer' },
              { label: 'AI_answer_change', key: 'AI_answer_change' },
              { label: 'question', key: 'question' },
              { label: 'questionImageLink', key: 'questionImageLink' },
              { label: 'correctanswer', key: 'correctanswer' },
              { label: 'incorrectanswer_0', key: 'incorrectanswer_0' },
              { label: 'incorrectanswer_1', key: 'incorrectanswer_1' },
              { label: 'incorrectanswer_2', key: 'incorrectanswer_2' },
              { label: 'notes', key: 'notes' },
              { label: 'softwarepage', key: 'softwarepage' },
              { label: 'flashmode', key: 'flashmode' },
              { label: 'flashmodeonly', key: 'flashmodeonly' },
              { label: 'documentpage', key: 'documentpage' },
              { label: 'deleted', key: 'deleted' },
              { label: 'link', key: 'link' },
              { label: 'AI_prompt', key: 'AI_prompt' },
              { label: 'aiAdminFeedback', key: 'aiAdminFeedback' },
              {
                label: 'correctanswerImageLink',
                key: 'correctanswerImageLink',
              },
              {
                label: 'incorrectanswerImageLink_0',
                key: 'incorrectanswerImageLink_0',
              },
              {
                label: 'incorrectanswerImageLink_1',
                key: 'incorrectanswerImageLink_1',
              },
              {
                label: 'incorrectanswerImageLink_2',
                key: 'incorrectanswerImageLink_2',
              },
              { label: 'status', key: 'status' },
              { label: 'sourceQuestion', key: 'sourceQuestion' },
              { label: 'chapter', key: 'chapter' },
              { label: 'deletedAt', key: 'deletedAt' },
              { label: 'up_votes', key: 'up_votes' },
              { label: 'down_votes', key: 'down_votes' },
            ]}
          />
        </div>
      </Row>
    );
  }
}

Header.propTypes = {
  reference: PropTypes.object,
  chapter: PropTypes.object,
  subtopics: PropTypes.array,
  onAddSubtopics: PropTypes.func,
  onShowDefinitions: PropTypes.func,
  onBack: PropTypes.func,
  onDeleteSubtopic: PropTypes.func,
};

export class Subtopic extends React.Component {
  render() {
    const { topic, subtopic, onDeleteSubtopic } = this.props;
    return (
      <div className="SubtopicBox">
        {`${topic.name} >> ${subtopic.name}`}{' '}
        {this.props.editPermission && (
          <i
            className="fa fa-times fa-lg mt-1"
            onClick={() => onDeleteSubtopic(subtopic)}
          />
        )}
      </div>
    );
  }
}
